import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/Checkbox'
import { Field, Textarea } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Select } from '@/components/ui/Select'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { placeName } from '@/lib/countries'
import { useStatusRecipient } from '@/features/cases/api/useCase'
import type { BulkStatusSendPayload } from '@/features/cases/api/useCase'
import {
  useClientStatusPreview,
  type ClientStatusSelection,
} from '@/features/cases/api/useStatusEmail'
import {
  NOTE_SETTLE_MS,
  StatusPreviewPanel,
} from '@/features/cases/components/CaseStatusSendSheet'
import { CAP, can } from '@/lib/can'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import type { CaseSummary, CurrentUser } from '@/types/domain'

/* Sending one client their status across several shipments in ONE email (FR-65).
 *
 * The list page's entry, and a different shape from the per-case send on the case
 * header: there, the recipient and the case are both already decided by where the
 * button is. Here neither is, so the sheet asks in the order the work happens —
 * WHO first, then WHICH of their shipments.
 *
 * Cases are filtered to the chosen client rather than listed with a client
 * column, because a list showing every case would let someone tick two clients'
 * shipments into one email. THE FILTER IS NOT THE GUARANTEE, and the comment here
 * used to say the server "drops" a foreign case: it did, and dropping was the
 * defect. internal/casefile now REFUSES a mixed selection outright (AC 3), so
 * this filter is convenience over a control that answers rather than trims.
 *
 * Two things this sheet no longer decides, both moved to the server where the
 * record is:
 *
 *   - THE RECIPIENT. It is resolved once from the client's own directory
 *     contacts, so "no address on file" is one condition for the whole send
 *     rather than a per-case verdict — which is what it always was, since every
 *     case here shares one client.
 *   - WHICH SHIPMENTS CAN BE REPORTED ON. A cancelled or financially closed
 *     shipment is skipped server-side: a message whose subject is where your
 *     cargo is has nothing to say about either. They are shown DISABLED with the
 *     reason rather than hidden, because a row that vanished is a row somebody
 *     goes looking for.
 */

const LANGUAGES = ['ru', 'en'] as const

/* The statuses internal/casefile leaves out of a consolidated send. Spelled here
 * so the sheet cannot offer what the server will skip; the server is still the
 * one that decides, and it reports every exclusion back. */
const UNREPORTABLE: readonly string[] = ['cancelled', 'financially-closed']

/** The consolidated preview's question (S-15), mounted inside the sheet's body
 *  so the settled note lives exactly as long as the open sheet — see the
 *  per-case `CaseStatusPreview`. An empty pick is not asked about: on the wire
 *  an empty `caseIds` means every case the client has, the opposite of what an
 *  empty pick means here (`useClientStatusPreview` refuses it too). */
function ClientStatusPreview({
  clientId,
  selection,
  note,
  language,
}: {
  clientId: string
  selection: ClientStatusSelection
  note: string
  language: 'en' | 'ru'
}) {
  const { t } = useTranslation()
  const settledNote = useDebouncedValue(note, NOTE_SETTLE_MS)
  const preview = useClientStatusPreview(
    clientId,
    selection,
    { note: settledNote, language },
    true,
  )
  const nothingTicked = selection !== 'all' && selection.length === 0

  return (
    <StatusPreviewPanel
      preview={preview}
      idle={nothingTicked ? t('cases.statusSend.preview.noneSelected') : undefined}
    />
  )
}

export function ClientStatusSendSheet({
  open,
  onClose,
  cases,
  user,
  onSubmit,
  pending,
  submitError,
}: {
  open: boolean
  onClose: () => void
  /** Every case in view. Filtered here by the chosen client. */
  cases: CaseSummary[]
  /** The sender, for whether they may read the client's contacts
   *  (`directory.contact.read`) — see `noContactAccess` below. */
  user: CurrentUser | undefined
  onSubmit: (values: BulkStatusSendPayload) => void
  pending?: boolean
  submitError?: string
}) {
  const { t, i18n } = useTranslation()

  const [clientId, setClientId] = useState('')
  const [note, setNote] = useState('')
  const [language, setLanguage] = useState<'en' | 'ru'>('ru')
  /* Which cases are ticked. `null` means "all of them" — the state this opens
     in, and what an empty caseIds sends. Kept distinct from an empty Set, which
     means somebody deliberately unticked everything and must not send. */
  const [selected, setSelected] = useState<Set<string> | null>(null)

  /* The clients that actually have cases in view, built from the rows rather
     than from the directory: a client with no shipments has nothing to be told
     about, and offering them would be an empty case list after a pick. */
  const clients = useMemo(() => {
    const byId = new Map<string, string>()
    for (const row of cases) {
      if (row.clientId && !byId.has(row.clientId)) {
        byId.set(row.clientId, row.clientName)
      }
    }
    return [...byId].map(([value, label]) => ({ value, label }))
  }, [cases])

  const clientCases = useMemo(
    () => (clientId ? cases.filter((row) => row.clientId === clientId) : []),
    [cases, clientId],
  )

  /* A shipment the server will not report on is never counted as selected —
     otherwise "3 selected" would promise three rows and send two. They stay
     VISIBLE and disabled, because a row that disappeared is a row somebody goes
     looking for, and the reason is worth reading.

     The test is the STATUS now, not a per-case address: the recipient is one
     client's, resolved server-side, so an address is a fact about the client and
     is reported by the notice below rather than per row. */
  const sendable = clientCases.filter((row) => !UNREPORTABLE.includes(row.status))
  const isSelected = (id: string) => selected === null || selected.has(id)
  const selectedCases = sendable.filter((row) => isSelected(row.id))

  const allSelected = selectedCases.length === sendable.length && sendable.length > 0
  const someSelected = selectedCases.length > 0 && !allSelected

  /* THE RECIPIENT IS ASKED FOR, not read off a row, and that is the fix for a
     notice that was wrong for every client in the product.

     This used to be `clientCases.find((row) => row.clientContactEmail)` — a
     field `GET /cases` has never served. casefile.CaseDTO carries no address
     and only the composed DETAIL does, so the find never matched, `noContact`
     was true whenever a client was chosen, and a complete directory record was
     reported as missing with the send disabled behind the notice. The type said
     otherwise, which is why it survived: `CaseSummary.clientContactEmail`
     described a field the server does not send.

     One request per client, resolved through the port the SEND resolves
     through — see the endpoint's own comment on why that identity matters. */
  /* A sender without `directory.contact.read` is refused the whole send by the
     server (403, story CI R9): the result would hand them the client's address.
     Their sheet says a colleague can send it — never "this client has no email",
     which would send them to a directory that is complete — and it does not ask
     who the recipient is, since the answer is not theirs to read. */
  const noContactAccess = !can(user, CAP.directoryContactRead)

  const { data: recipient, isPending: recipientPending } = useStatusRecipient(
    noContactAccess ? '' : clientId,
  )
  const recipientEmail = noContactAccess ? '' : (recipient?.email?.trim() ?? '')

  /* Two different notices, because they need two different actions. No address
     sends somebody to the directory; nothing reportable is not anybody's mistake
     — every shipment this client has is cancelled or closed, and there is nothing
     to tell them. Conflating them under one message is what the single
     `sendable.length === 0` check used to do, and it pointed the reader at a
     directory that was already correct.

     Withheld WHILE THE ANSWER IS IN FLIGHT. Claiming "no contact" before the
     server has answered would flash the refusal on every client pick, and a
     notice that appears and then withdraws itself teaches people to ignore it. */
  const noContact =
    !noContactAccess && clientId !== '' && !recipientPending && recipientEmail === ''
  const nothingReportable = clientId !== '' && clientCases.length > 0 && sendable.length === 0

  const reset = () => {
    setClientId('')
    setNote('')
    setLanguage('ru')
    setSelected(null)
  }

  const toggle = (id: string) => {
    setSelected((current) => {
      // Materialise "all" into a real set on the first untick, so the two
      // states never disagree about what is on screen.
      const next = new Set(current ?? sendable.map((row) => row.id))
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <FormSheet
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title={t('cases.clientStatusSend.title')}
      submitLabel={
        selectedCases.length > 0
          ? t('cases.clientStatusSend.submitCount', { count: selectedCases.length })
          : t('cases.statusSend.submit')
      }
      onSubmit={() =>
        onSubmit({
          clientId,
          // All-selected sends an empty list, which the server reads as "every
          // case this client has" — so a case created between opening the sheet
          // and sending is included rather than silently skipped.
          caseIds: allSelected ? [] : selectedCases.map((row) => row.id),
          note: note.trim(),
          language,
        })
      }
      pending={pending}
      /* `noContact` blocks the send explicitly rather than by arithmetic. The
         server refuses it anyway — a client with no address on file answers 422
         on clientContactEmail — and this is the courtesy that stops somebody
         pressing a button whose only possible outcome is that refusal. */
      disabled={clientId === '' || selectedCases.length === 0 || noContact}
      /* The access refusal is one the reader acts on by asking a colleague, so
         it is `blockedReason` (tab stop, tooltip) as on the per-case sheet and
         the offer send, and the notice repeats it for a phone. */
      blockedReason={noContactAccess ? t('cases.statusSend.noContactAccess') : undefined}
      error={submitError}
      notice={
        noContactAccess
          ? t('cases.statusSend.noContactAccess')
          : noContact
            ? t('cases.clientStatusSend.noContact')
            : nothingReportable
              ? t('cases.clientStatusSend.nothingReportable')
              : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('cases.clientStatusSend.client')} required>
          {(props) => (
            <Select
              {...props}
              value={clientId}
              onChange={(next) => {
                setClientId(next)
                // A fresh client starts with everything selected: "tell this
                // client about their shipments" is the common intent, and
                // unticking a few is less work than ticking all of them.
                setSelected(null)
              }}
              options={clients}
              placeholder={t('cases.clientStatusSend.clientPlaceholder')}
              searchable={clients.length > 8}
            />
          )}
        </Field>

        {clientId === '' ? (
          /* Nothing is chosen yet, so the case list would be an empty box with
             a heading. Saying what happens next is more use than a blank. */
          <p className="text-text-muted text-[13px] leading-snug">
            {t('cases.clientStatusSend.pickClientFirst')}
          </p>
        ) : (
          <>
            {recipientEmail !== '' ? (
              <div className="border-line bg-surface-sunken flex flex-col gap-1.5 rounded-lg border px-4 py-3">
                <span className="asa-eyebrow">{t('cases.statusSend.recipient')}</span>
                <span className="text-text-heading text-[13px] leading-snug">
                  {recipient?.name ? (
                    <>
                      {recipient.name}{' '}
                      <span className="text-text-muted">&lt;{recipientEmail}&gt;</span>
                    </>
                  ) : (
                    recipientEmail
                  )}
                </span>
              </div>
            ) : null}

            {clientCases.length > 0 ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="asa-eyebrow">
                    {t('cases.clientStatusSend.cases')}
                  </span>
                  <span className="text-text-muted asa-num text-[12px]">
                    {t('cases.clientStatusSend.selectedCount', {
                      selected: selectedCases.length,
                      total: sendable.length,
                    })}
                  </span>
                </div>

                {/* Select-all sits ABOVE the list and carries the
                    indeterminate state, so the box reflects the list rather
                    than only driving it. */}
                {sendable.length > 1 ? (
                  <div className="border-line border-b pb-2">
                    <Checkbox
                      label={t('cases.clientStatusSend.selectAll')}
                      checked={allSelected}
                      indeterminate={someSelected}
                      onCheckedChange={() => setSelected(allSelected ? new Set() : null)}
                    />
                  </div>
                ) : null}

                {/* Bounded and scrollable: a client with forty shipments must
                    not push the note and the send button off the sheet. */}
                <ul className="asa-scroll flex max-h-64 flex-col gap-1 overflow-y-auto">
                  {clientCases.map((row) => {
                    const sendableRow = !UNREPORTABLE.includes(row.status)
                    return (
                      <li key={row.id} className="flex items-center gap-3 py-1">
                        <Checkbox
                          label={row.number}
                          checked={sendableRow && isSelected(row.id)}
                          disabled={!sendableRow}
                          /* The reason replaces the route on a row that cannot
                             be sent. `disabled` makes a title unhoverable and
                             untabbable, so a refusal the reader could act on has
                             to be text they can already see. */
                          hint={
                            sendableRow
                              ? `${placeName(row.origin, i18n.language)} → ${placeName(row.destination, i18n.language)}`
                              : t('cases.clientStatusSend.notReportable')
                          }
                          onCheckedChange={() => toggle(row.id)}
                          className="min-w-0 flex-1"
                        />
                        <span className="shrink-0">
                          <CaseStatusPill status={row.status} />
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}

            <Field
              label={t('cases.statusSend.language')}
              hint={t('cases.statusSend.languageHint')}
            >
              {(props) => (
                <Select
                  {...props}
                  value={language}
                  onChange={setLanguage}
                  options={LANGUAGES.map((value) => ({
                    value,
                    label: t(
                      value === 'ru'
                        ? 'common.language.russian'
                        : 'common.language.english',
                    ),
                  }))}
                />
              )}
            </Field>

            <Field
              label={t('cases.statusSend.note')}
              hint={t('cases.clientStatusSend.noteHint')}
            >
              {(props) => (
                <Textarea
                  {...props}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={t('cases.statusSend.notePlaceholder')}
                  rows={3}
                />
              )}
            </Field>

            {/* The email exactly as this send would mail it (S-15), with the
                payload the send would carry — "all" as the send's empty list,
                the trimmed note. Only once the server has named a recipient the
                sender may read: without contact access, or with no address,
                the send is refused up front and the notice says why; a client
                with nothing reportable has no email to show. */}
            {!noContactAccess && recipientEmail !== '' && !nothingReportable ? (
              <ClientStatusPreview
                clientId={clientId}
                selection={allSelected ? 'all' : selectedCases.map((row) => row.id)}
                note={note.trim()}
                language={language}
              />
            ) : null}
          </>
        )}
      </div>
    </FormSheet>
  )
}
